import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { useUpsertMedia } from '@/api/queries'
import { useConfig } from '@/config/ConfigProvider'
import { isMockConfig } from '@/config/runtime'
import { Button } from '@/components/ui/Button'
import { Input, Select } from '@/components/ui/Field'
import { EmptyState, Skeleton } from '@/components/ui/States'
import { useToast } from '@/components/ui/Toast'
import { ReviewSurface } from '@/components/review/ReviewSurface'
import { StatusTabs } from '@/components/review/StatusTabs'
import { useReviewCollection } from '@/components/review/useReviewCollection'
import { MediaCard } from './MediaCard'
import { MediaDrawer } from './MediaDrawer'
import { UploadModal } from './UploadModal'
import { cn } from '@/lib/cn'
import type { MediaItem } from '@/types'

type TypeFilter = 'all' | 'image' | 'video'

const searchText = (m: MediaItem) => `${m.title ?? ''} ${m.description ?? ''}`

const TYPE_TABS: TypeFilter[] = ['all', 'image', 'video']

/** Cards rendered per "Show more": a full crawl finds well over a thousand. */
const PAGE_SIZE = 60

/**
 * Renders a grid rather than a DataTable — which is exactly why
 * `ReviewSurface` takes `children` instead of a column config.
 *
 * No bulk bar: selection over a grid of cards is a worse gesture than the
 * per-card verdict buttons, and the cards already carry approve/reject.
 *
 * Approval is the gate (docs/03-page-specs.md §7.5). The agent's `usable`
 * judgement only decides what is shown by default: unusable assets (logos,
 * icons, charts) are left out of the tabs and their counts until asked for.
 *
 * Adding media is an upload, which the API cannot store yet (APP-138), so the
 * button is on the mock only.
 */
export function MediaPage() {
  const { clientId } = useParams()
  const upsert = useUpsertMedia(clientId!)
  const toast = useToast()
  const canUpload = isMockConfig(useConfig())

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [showUnusable, setShowUnusable] = useState(false)
  const [editing, setEditing] = useState<MediaItem | null>(null)
  const [uploading, setUploading] = useState(false)
  const [limit, setLimit] = useState(PAGE_SIZE)

  const byType = useCallback(
    (m: MediaItem) => typeFilter === 'all' || m.kind === typeFilter,
    [typeFilter],
  )
  const usableOnly = useCallback((m: MediaItem) => showUnusable || m.usable, [showUnusable])

  const collection = useReviewCollection<MediaItem>(clientId!, 'media', {
    searchText,
    noun: 'media item',
    extraFilter: byType,
    scope: usableOnly,
  })

  const { status, query, tagFilter } = collection
  useEffect(() => setLimit(PAGE_SIZE), [status, query, tagFilter, typeFilter, showUnusable])

  const shown = collection.rows.slice(0, limit)

  const addButton = (size?: 'sm') =>
    canUpload && (
      <Button size={size} variant={size ? undefined : 'primary'} onClick={() => setUploading(true)}>
        {!size && <Plus size={14} aria-hidden />}
        Add media
      </Button>
    )

  return (
    <>
      <ReviewSurface
        title="Media library"
        description="Images and video from the crawl. Only approved media is retrievable by the agent."
        headerActions={addButton()}
        filters={
          <>
            {/* Type is the primary filter, so it leads. */}
            <div className="inline-flex rounded-[6px] border border-rule-strong p-0.5">
              {TYPE_TABS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTypeFilter(t)}
                  aria-pressed={typeFilter === t}
                  className={cn(
                    'rounded-[4px] px-2.5 py-1 font-head text-[12px] font-semibold capitalize transition-colors',
                    typeFilter === t
                      ? 'bg-brand-blue-100 text-brand-blue-800'
                      : 'text-ink-muted hover:bg-surface-sunk',
                  )}
                >
                  {t === 'all' ? 'All media' : `${t}s`}
                </button>
              ))}
            </div>

            <div className="relative">
              <Search
                size={13}
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
              />
              <Input
                value={collection.query}
                onChange={(e) => collection.setQuery(e.target.value)}
                placeholder="Search titles and descriptions"
                aria-label="Search media"
                className="h-8 w-60 pl-7 text-[13px]"
              />
            </div>

            <Select
              value={collection.tagFilter}
              onChange={(e) => collection.setTagFilter(e.target.value)}
              aria-label="Filter by tag"
              className="h-8 w-36 text-[13px]"
            >
              <option value="all">All tags</option>
              {collection.tagPool.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>

            <label className="inline-flex items-center gap-1.5 text-[13px] text-ink-muted">
              <input
                type="checkbox"
                checked={showUnusable}
                onChange={(e) => setShowUnusable(e.target.checked)}
              />
              Show unusable
            </label>
          </>
        }
        tabs={
          <StatusTabs
            value={collection.status}
            counts={collection.counts}
            onChange={collection.setStatus}
          />
        }
      >
        <div className="p-4">
          {collection.isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-[260px]" />
              ))}
            </div>
          ) : collection.rows.length === 0 ? (
            <EmptyState
              title="No media matches these filters"
              description={
                collection.all.length === 0 ? 'The last crawl did not find any usable media.' : undefined
              }
              action={addButton('sm') || undefined}
            />
          ) : (
            <>
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((item) => (
                  <MediaCard
                    key={item.id}
                    item={item}
                    tagPool={collection.tagPool}
                    onOpen={() => setEditing(item)}
                    onFavorite={(next) => collection.setFavoriteFor(item.id, next)}
                    onTags={(tags) => upsert.mutate({ id: item.id, tags })}
                    onReview={(next) => collection.setStatusFor(item.id, next)}
                    onCopyUrl={() => {
                      navigator.clipboard?.writeText(item.url)
                      toast.push('Media URL copied.')
                    }}
                  />
                ))}
              </ul>
              {collection.rows.length > shown.length && (
                <div className="mt-4 flex justify-center">
                  <Button size="sm" onClick={() => setLimit((n) => n + PAGE_SIZE)}>
                    Show more ({collection.rows.length - shown.length} left)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </ReviewSurface>

      <MediaDrawer
        clientId={clientId!}
        item={editing}
        tagPool={collection.tagPool}
        onClose={() => setEditing(null)}
        onSave={async (patch) => {
          try {
            await upsert.mutateAsync(patch)
          } catch {
            // The drawer stays open with the draft, so nothing typed is lost.
            toast.push('Could not save the media item.', 'error')
            return
          }
          setEditing(null)
          toast.push('Media saved.')
        }}
        onReview={(id, next) => {
          collection.setStatusFor(id, next)
          setEditing(null)
        }}
        onFavorite={collection.setFavoriteFor}
      />

      {canUpload && (
        <UploadModal
          open={uploading}
          tagPool={collection.tagPool}
          onClose={() => setUploading(false)}
          onUpload={async (files) => {
            for (const file of files) {
              await upsert.mutateAsync({
                kind: file.mediaType,
                url: file.url,
                description: file.description,
                tags: file.tags,
              })
            }
            setUploading(false)
            toast.push(`Uploaded ${files.length} file${files.length === 1 ? '' : 's'}.`)
          }}
        />
      )}

      {collection.deleteDialog}
    </>
  )
}
